import React, { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { usePlan } from "@/_actions/authActions";
import PropTypes from "prop-types";

import Pagination from "@/components/navigation/Pagination";
import {
  useContacts,
  contactsParamsAtom,
  initialParams,
  usePipelines,
  useComments,
  useContactPipelineHistory,
} from "@/_actions/contactActions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import downloadPropertyPdf from "@/utils/downloadPdf";
import { toast } from "sonner";
import apiClient from "@/utils/apiClient";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";
import CreateContact from "./forms/CreateContact";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Dot, MessageSquare, PhoneOutgoing, Loader2Icon, PlusIcon } from "lucide-react";
import TextEditor from "@/components/forms/TextEditor";
import { Badge } from "@/components/ui/badge";
import { NumericFormat } from "react-number-format";
import { Forward, Heart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import Analytics from "./Analytics";
import Filters from "./Filters";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ContactsTable from "./table/data-table";
import ContactsList from "./list/ContactsList";
import PhoneValidationBadge, {
  getPhoneValidationStatus,
  getWhatsappPhone,
  getTelPhone,
} from "./PhoneValidationBadge";
import { getColumns } from "./table/columns";
import { FileText } from "lucide-react";
import { Formik, Field, Form } from "formik";

export default function Index() {
  const [contactsParams, setContactParams] = useAtom(contactsParamsAtom);
  const { data: planData } = usePlan();
  const { plan } = planData || {};
  const { data: contacts, mutate } = useContacts(contactsParams);
  const [selected, setSelected] = useState({});
  const [managedLeads, setManagedLeads] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const leadsQuantity = plan?.plan_addons?.leads_quantity ?? 0;
  const { data: pipelines, error } = usePipelines();
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    const isManagedLeads =
      plan?.plan_addons.managed_leads === true || plan?.plan_addons.managed_leads === "true";

    if (isManagedLeads) {
      setManagedLeads(true);
      setContactParams((prev) => ({ ...prev, managed_leads: true }));
    } else {
      setManagedLeads(false);
      setContactParams((prev) => {
        const newParams = { ...prev };
        delete newParams.managed_leads;
        return newParams;
      });
    }
  }, [plan, setContactParams]);

  // Add this new useEffect to update selected contact when contacts data changes
  useEffect(() => {
    if (contacts?.results && selected?.id) {
      const updatedContact = contacts.results.find((contact) => contact.id === selected.id);
      if (updatedContact) {
        setSelected(updatedContact);
      }
    }
  }, [contacts, selected?.id]);

  const handleReset = () => {
    if (managedLeads) {
      setContactParams({
        page: 1,
        status: 1,
        managed_leads: true,
      });
    } else {
      setContactParams(initialParams);
    }
    setResetKey((prev) => prev + 1);
  };

  const changeStage = async (stage) => {
    // Prevent unnecessary updates if the stage hasn't changed
    if (selected?.pipeline_status?.stage?.id === stage) {
      console.log("Stage hasn't changed, skipping update.");
      return;
    }

    setLoading(true);
    try {
      await apiClient.patch(`contacts/${selected.id}/pipeline-stage/`, {
        stage,
      });
      // This will trigger a refetch of contacts data
      // which will then update the selected contact via the useEffect above
      mutate();
    } finally {
      setLoading(false);
    }
  };

  const [sheetStatus, toggleSheet] = React.useState(false);

  return (
    <div>
      <div className="border-b border-zinc-300 pb-3 ">
        <div className="ml-12 border-l border-zinc-300 pl-4">
          <h1 className="text-2xl font-bold leading-tight tracking-tight text-zinc-800">
            Oportunidades
          </h1>
        </div>
      </div>
      <div key={resetKey} className="w-full">
        <div className="flex w-full">
          <div className="w-full">
            <div>
              <div>
                <Sheet open={sheetStatus} onOpenChange={toggleSheet}>
                  <SheetTrigger asChild>
                    <Button>
                      <PlusIcon className="mr-2 h-4 w-4" />
                      Nueva Oportunidad
                    </Button>
                  </SheetTrigger>
                  <SheetContent side={isMobile ? "bottom" : "right"}>
                    <SheetHeader>
                      <SheetTitle>Crear Nueva Oportunidad</SheetTitle>
                      <SheetDescription>
                        Agregar una nueva oportunidad a tus contactos.
                      </SheetDescription>
                    </SheetHeader>
                    <div className="my-5">
                      <CreateContact
                        callBack={() => {
                          mutate();
                          toggleSheet(false);
                        }}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
            <div className="">
              <div className="col-span-4 w-full my-5 bg-white rounded-2xl p-4">
                <Filters
                  pipelines={pipelines}
                  onReset={handleReset}
                  onSubmit={(values) =>
                    setContactParams(() => {
                      if (values.status !== "all") {
                        return { ...values };
                      } else {
                        const r = { ...values };
                        delete r.status;
                        return r;
                      }
                    })
                  }
                />
              </div>
            </div>
          </div>
          {plan?.plan_addons.contact_analytics ? (
            <div className="h-full">
              <Analytics managedLeads={managedLeads} leadsQuantity={leadsQuantity} />
            </div>
          ) : null}
        </div>
        {managedLeads && leadsQuantity <= 5 && (
          <div className="w-full border border-red-200 bg-red-50 text-red-600 p-3 mt-3 rounded-md text-center font-semibold">
            {leadsQuantity > 0 ? (
              <>
                ⚠️ ¡Atención! Solo te quedan <strong className=" underline">{leadsQuantity}</strong>{" "}
                leads disponibles. Comunícate con tu asesor para adquirir más.
              </>
            ) : (
              <>⚠️ ¡Alerta! Ya te quedaste sin leads.</>
            )}
          </div>
        )}

        <div className="bg-white p-4 rounded-2xl">
          {isMobile ? (
            <ContactsList
              onSelect={(contact) => {
                setSelected(contact);
                setOpen(true);
              }}
              pipelines={pipelines}
              data={contacts?.results || []}
            />
          ) : (
            <ContactsTable
              data={contacts?.results || []}
              columns={getColumns(pipelines, mutate)}
              onSelect={(row) => {
                setSelected(row);
                setOpen(true);
              }}
            />
          )}
        </div>
        <div className="bg-white p-4 rounded-2xl mt-3">
          <Pagination
            total={contacts?.count}
            type={"Contacto"}
            current={contactsParams.page}
            next={contacts?.next}
            previous={contacts?.previous}
            pageSize={10}
            onPageChange={(page) => setContactParams((prev) => ({ ...prev, page }))}
          />
        </div>

        {isMobile && selected?.id ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">Edit Profile</Button>
            </DialogTrigger>
            <DialogContent className="w-full h-full overflow-y-scroll">
              <DialogHeader>
                <Avatar>
                  <AvatarFallback>
                    {selected?.name
                      ? selected.name.split(" ")[0]?.[0] + (selected.name.split(" ")[1]?.[0] || "")
                      : "?"}
                  </AvatarFallback>
                </Avatar>
                <DialogTitle> {selected.name}</DialogTitle>
                <Select
                  onValueChange={changeStage}
                  value={selected?.pipeline_status?.stage?.id || ""}
                  disabled={loading}
                  closeOnSelect={true}
                >
                  <SelectTrigger className="w-full">
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <Loader2Icon className="animate-spin h-4 w-4" />
                        <span>Actualizando...</span>
                      </div>
                    ) : (
                      <SelectValue placeholder="Seleccionar Estatus" />
                    )}
                  </SelectTrigger>
                  <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                    {pipelines?.length > 0 &&
                      pipelines[0].stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          <span className="flex items-center ">
                            <Dot
                              className="h-10 w-10 "
                              style={{
                                color: stage.color || "#ccc",
                              }}
                            />
                            {stage.name}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </DialogHeader>
              <ContactDetails contact={selected} />
            </DialogContent>
          </Dialog>
        ) : (
          <Drawer
            open={open}
            setOpen={setOpen}
            direction={isMobile ? "bottom" : "right"}
            dismissible="true"
            onOpenChange={setOpen}
          >
            {selected.name && (
              <DrawerContent>
                <DrawerHeader className="flex items-center justify-between">
                  <Avatar>
                    <AvatarFallback>
                      {selected?.name
                        ? selected.name.split(" ")[0]?.[0] +
                          (selected.name.split(" ")[1]?.[0] || "")
                        : "?"}
                    </AvatarFallback>
                  </Avatar>

                  <DrawerTitle>{selected.name}</DrawerTitle>

                  <DrawerDescription>
                    <Select
                      value={selected?.pipeline_status?.stage?.id || ""}
                      disabled={loading}
                      closeOnSelect={true}
                      onValueChange={changeStage}
                    >
                      <SelectTrigger className="w-full">
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <Loader2Icon className="animate-spin h-4 w-4" />
                            <span>Actualizando...</span>
                          </div>
                        ) : (
                          <SelectValue placeholder="Seleccionar Estatus" />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {pipelines[0]
                          ? pipelines[0].stages.map((stage, index) => (
                              <SelectItem key={index} value={stage.id}>
                                <span className="flex items-center">
                                  <Dot
                                    className="h-10 w-10 "
                                    style={{
                                      color: stage.color || "#ccc",
                                    }}
                                  />
                                  {stage.name}
                                </span>
                              </SelectItem>
                            ))
                          : null}
                      </SelectContent>
                    </Select>
                  </DrawerDescription>
                </DrawerHeader>

                <ContactDetails contact={selected} />
              </DrawerContent>
            )}
          </Drawer>
        )}
      </div>
    </div>
  );
}

function GestureGuard({ className = "", children }) {
  const stop = (e) => {
    // corta propagación en React y en el evento nativo
    e.stopPropagation();
    if (e.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  return (
    <div
      className={`select-text touch-pan-y overscroll-contain ${className}`}
      onPointerDownCapture={stop}
      onPointerMoveCapture={stop}
      onTouchStartCapture={stop}
      onTouchMoveCapture={stop}
      onMouseDownCapture={stop}
      onMouseMoveCapture={stop}
    >
      {children}
    </div>
  );
}
GestureGuard.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const ContactDetails = ({ contact }) => {
  const { data: comments, mutate: mutateComments } = useComments(contact?.id);
  const { data: pipelineHistory, mutate: mutatePipelineHistory } = useContactPipelineHistory(
    contact?.id,
  );

  return (
    <Tabs defaultValue="info" className="flex flex-col h-full max-w-full overflow-scroll">
      <TabsList>
        <TabsTrigger value="info">Información</TabsTrigger>
        <TabsTrigger value="comments">Comentarios</TabsTrigger>
        <TabsTrigger value="activity">Actividad</TabsTrigger>
      </TabsList>
      <TabsContent value="info" className=" ">
        <GestureGuard className="bg-gray-100 p-5 rounded-2xl mt-2 ">
          {/* activity */}
          <h3 className="text-lg font-medium leading-6 text-gray-900">Datos</h3>
          <div>
            <div className="flex flex-col gap-2 gap-y-5 mt-2">
              <ul className="list-disc pl-4">
                <li>Nombre: {contact.name}</li>
                <li>
                  Email:&nbsp;
                  <a
                    className="text-blue-600 underline hover:text-blue-800 hover:cursor-pointer"
                    href={`mailto:${contact.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {contact.email}
                  </a>
                </li>
                <li className="flex items-center gap-2 flex-wrap">
                  <span>
                    Teléfono:&nbsp;
                    <a
                      className="text-blue-600 underline hover:text-blue-800 hover:cursor-pointer"
                      href={`tel:${getTelPhone(contact.phone_validation, contact.phone)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {contact.phone}
                    </a>
                  </span>
                  <PhoneValidationBadge validation={contact.phone_validation} />
                </li>
              </ul>
              {contact.phone_validation && (
                <div className="rounded-lg bg-white border p-3 text-sm grid grid-cols-2 gap-x-3 gap-y-1">
                  <span className="text-gray-500">Tipo de línea</span>
                  <span className="font-medium">
                    {getPhoneValidationStatus(contact.phone_validation).label}
                  </span>
                  {contact.phone_validation.carrier && (
                    <>
                      <span className="text-gray-500">Operador</span>
                      <span>{contact.phone_validation.carrier}</span>
                    </>
                  )}
                  {(contact.phone_validation.city || contact.phone_validation.country) && (
                    <>
                      <span className="text-gray-500">Ubicación</span>
                      <span>
                        {[contact.phone_validation.city, contact.phone_validation.country]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </>
                  )}
                  {contact.phone_validation.cleansed_phone && (
                    <>
                      <span className="text-gray-500">Número internacional</span>
                      <span>{contact.phone_validation.cleansed_phone}</span>
                    </>
                  )}
                </div>
              )}
              <div className="flex flex-col md:flex-row gap-2 my-3">
                <Button variant="outline" className="w-full">
                  <MessageSquare className=" h-4 w-4" />
                  <a
                    href={`https://wa.me/${getWhatsappPhone(contact.phone_validation, contact.phone)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Iniciar conversación en WhatsApp
                  </a>
                </Button>

                <Button variant="outline" className="w-full">
                  <PhoneOutgoing className=" h-4 w-4" />
                  <a
                    href={`tel:${getTelPhone(contact.phone_validation, contact.phone)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Llamar
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </GestureGuard>
        {contact.image_cover && contact.property_prices.length > 0 ? (
          <section className="flex flex-col gap-4">
            <Card className="w-full">
              <CardHeader>
                <CardTitle>{contact.property_title}</CardTitle>
                <CardDescription>
                  {contact.property_prices.map((price, index) => {
                    return (
                      <p key={index}>
                        {price.operation}:
                        <NumericFormat
                          displayType="text"
                          thousandSeparator={true}
                          prefix={"$"}
                          value={price.price}
                        />
                        {price.currency}
                      </p>
                    );
                  })}
                </CardDescription>
                <CardAction></CardAction>
              </CardHeader>
              <CardContent>
                <img src={contact.image_cover} alt="property_image" />
              </CardContent>
              <CardFooter>
                {/* show action buttons 1. download pdf, 2. copy property link */}
                <div className="flex justify-between">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        toast("Descargando ficha...");
                        downloadPropertyPdf(contact.property_id)
                          .then((e) => {
                            toast.success("Ficha descargada correctamente");
                          })
                          .catch((e) => {
                            toast.error("Ha ocurrido un error");
                          });
                      }}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Descargar Ficha
                    </Button>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </section>
        ) : null}
      </TabsContent>
      <TabsContent value="comments" className="flex flex-col flex-grow ">
        <>
          {/* MESSAGES SECTION */}
          <section className="bg-slate-100 p-2 flex flex-grow flex-col">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Comentarios</h3>
            <div>
              {comments?.map((message, index) => {
                return (
                  <>
                    <hr className="my-4 border-gray-200" />
                    <div key={message.id} className="flex flex-col space-y-2 prose prose-xs">
                      <div className="flex  space-x-2">
                        <Avatar className="flex">
                          <AvatarFallback>{message.commentator_name[0]}</AvatarFallback>
                        </Avatar>

                        <div className="flex flex-grow flex-col  w-full">
                          <div className="flex items-center justify-between w-full">
                            <div className="text-sm capitalize font-semibold text-gray-900">
                              {message.commentator_name}
                            </div>
                            <div className="flex-grow flex"></div>
                            <div className="text-xs text-gray-500">
                              {formatDistanceToNow(new Date(message.created_at), {
                                locale: es,
                              })}
                            </div>
                          </div>
                          <p
                            className="text-sm"
                            dangerouslySetInnerHTML={{
                              __html: message.content,
                            }}
                          ></p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}
            </div>
          </section>

          <Formik
            initialValues={{
              description: "",
            }}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                await apiClient.post(`/contacts/${contact.id}/comments/`, {
                  content: values.description,
                });
                mutateComments();
                resetForm();
                setSubmitting(false);
              } catch (error) {
                console.error("Error sending message:", error);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form className=" w-full space-y-6 p-5 rounded-t-4xl  ">
                <Field
                  className="input"
                  as={TextEditor}
                  placeholder="Agrega un comentario"
                  name="description"
                ></Field>
                <Button className="w-full" disabled={isSubmitting} type="submit">
                  Agregar
                </Button>
              </Form>
            )}
          </Formik>
        </>
        <DrawerFooter></DrawerFooter>
      </TabsContent>
      <TabsContent value="activity" className="flex flex-col flex-grow ">
        <>
          {/* MESSAGES SECTION */}
          <section className="bg-slate-100 p-2 flex flex-grow flex-col">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Historial de actividad</h3>

            <div className="flex flex-col gap-2 gap-y-5 mt-5">
              {pipelineHistory?.map((pipeMovement, index) => {
                return (
                  <div key={index} className="flex items-start space-x-2 text-sm">
                    {pipeMovement.executive_name} movìo de{" "}
                    <Badge
                      className="mx-2"
                      style={{
                        backgroundColor: pipeMovement.from_stage_color,
                      }}
                    >
                      {pipeMovement.from_stage_name}
                    </Badge>{" "}
                    a{" "}
                    <Badge
                      className="mx-2"
                      style={{
                        backgroundColor: pipeMovement.to_stage_color,
                      }}
                    >
                      {pipeMovement.to_stage_name}
                    </Badge>{" "}
                    hace &nbsp;
                    {formatDistanceToNow(new Date(pipeMovement.moved_at), {
                      locale: es,
                    })}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      </TabsContent>
    </Tabs>
  );
};

ContactDetails.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string,
    phone_validation: PropTypes.shape({
      provider: PropTypes.string,
      cleansed_phone: PropTypes.string,
      national_phone: PropTypes.string,
      carrier: PropTypes.string,
      phone_type: PropTypes.string,
      country: PropTypes.string,
      country_code: PropTypes.string,
      country_code_numeric: PropTypes.string,
      city: PropTypes.string,
    }),
    address: PropTypes.string,
    image_cover: PropTypes.string,
    property_id: PropTypes.string,
    property_title: PropTypes.string,
    property_prices: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  pipelineHistory: PropTypes.arrayOf(
    PropTypes.shape({
      executive_name: PropTypes.string.isRequired,
      from_stage_name: PropTypes.string.isRequired,
      from_stage_color: PropTypes.string.isRequired,
      to_stage_name: PropTypes.string.isRequired,
      to_stage_color: PropTypes.string.isRequired,
      moved_at: PropTypes.string.isRequired,
    }),
  ).isRequired,
};
